import { useMemo, useState } from "react";
import { SectionShell } from "@/pages/SectionShell";
import { Button } from "@/components/Button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EmptyState } from "@/components/EmptyState";
import { useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useCollection } from "@/lib/hooks/useCollection";
import { call, CALLABLES } from "@/services/callables";
import type { Institution, User } from "@/types";

/**
 * Finding one person across every institution.
 *
 * The only screen in the console that spans tenants, and it exists because a
 * support question almost always arrives as a name and nothing else. What it
 * shows is an account and its access — **nothing anybody wrote is reachable
 * from here, with or without support access**, and the line under the table
 * says so rather than leaving an admin to wonder whether they simply have not
 * found it yet.
 */

const MIN_QUERY_LENGTH = 2;

export function UsersPage() {
  const t = useT();
  const { success, info, showError } = useToast();
  const { rows: users, loading } = useCollection<User>("users");
  const { rows: institutions } = useCollection<Institution>("institutions");

  const [needle, setNeedle] = useState("");
  const [disabling, setDisabling] = useState<User | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const nameOf = useMemo(() => {
    const names = new Map(institutions.map((institution) => [institution.id, institution.name]));
    return (id: string | null) => (id ? (names.get(id) ?? id) : "");
  }, [institutions]);

  const query = needle.trim().toLowerCase();
  const shown = useMemo(() => {
    if (query.length < MIN_QUERY_LENGTH) return [];
    return users
      .filter((user) => {
        const haystack = [user.displayName, user.username, user.email ?? "", user.uid]
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      })
      .slice(0, 100);
  }, [users, query]);

  return (
    <SectionShell titleKey="users.title">
      <label className="block max-w-xl">
        <span className="field-label">{t("users.search")}</span>
        <input
          type="search"
          className="field-input"
          value={needle}
          autoFocus
          onChange={(event) => setNeedle(event.target.value)}
        />
      </label>
      <p className="mt-2 max-w-2xl text-sm text-ink-muted">{t("users.searchHint")}</p>

      <div className="mt-6">
        {loading ? (
          <p className="text-sm text-ink-muted">{t("common.loading")}</p>
        ) : query.length < MIN_QUERY_LENGTH ? (
          <EmptyState titleKey="users.typeToSearch" />
        ) : shown.length === 0 ? (
          <EmptyState titleKey="users.empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">{t("users.title")}</caption>
              <thead>
                <tr className="border-b border-paper-deep text-left text-ink-muted">
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("users.column.name")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("users.column.role")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("users.column.institution")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("users.column.username")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">{t("users.column.status")}</th>
                  <th scope="col" className="px-3 py-2.5 font-medium">
                    <span className="sr-only">{t("common.edit")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((user) => (
                  <tr key={user.uid} className="border-b border-paper-warm last:border-0">
                    <td className="px-3 py-3 text-ink">{user.displayName}</td>
                    <td className="px-3 py-3 text-ink-muted">{user.role}</td>
                    <td className="px-3 py-3 text-ink-muted">{nameOf(user.institutionId)}</td>
                    <td className="px-3 py-3 text-ink-muted">{user.username}</td>
                    <td className="px-3 py-3 text-ink-muted">
                      {user.disabled
                        ? t("institutions.state.suspended")
                        : t("institutions.state.active")}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Button
                        variant="ghost"
                        busy={busy}
                        onClick={async () => {
                          setBusy(true);
                          try {
                            const result = await call<
                              { uid: string },
                              { temporaryPassword: string }
                            >(CALLABLES.resetUserPassword, { uid: user.uid });
                            // Once. There is nowhere to look it up afterwards.
                            info("users.passwordIssued", {
                              name: user.displayName,
                              password: result.temporaryPassword,
                            });
                            info("users.passwordIssuedWarning");
                          } catch (error) {
                            showError(error);
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        {t("users.resetPassword")}
                      </Button>
                      <Button variant="ghost" onClick={() => setDisabling(user)}>
                        {t(user.disabled ? "users.enable" : "users.disable")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="mt-6 max-w-2xl text-sm text-ink-muted">{t("users.noContent")}</p>

      <ConfirmDialog
        open={disabling !== undefined}
        onClose={() => setDisabling(undefined)}
        onConfirm={async (reason) => {
          if (!disabling) return;
          await call(CALLABLES.setUserDisabled, {
            uid: disabling.uid,
            disabled: !disabling.disabled,
            reason,
          });
          success("common.saved");
        }}
        titleKey="users.disableTitle"
        titleParams={{ name: disabling?.displayName ?? "" }}
        bodyKey="users.disableBody"
        expected={disabling?.displayName ?? ""}
        confirmKey="users.confirm"
        mismatchKey="users.mismatch"
        withReason
      />
    </SectionShell>
  );
}
